import {config} from "../../../config";
import {createApi} from "@reduxjs/toolkit/dist/query/react";
import {allPage, OffsetPagination, PageQuery, PageResult, toPagination} from "@tracabois/utils";
import {
  OrganizationType,
  OrganizationTypeCreateCommand,
  OrganizationTypeCreatedEvent,
  OrganizationTypeDeleteCommand,
  OrganizationTypeDeletedEvent,
  OrganizationTypeId,
  OrganizationTypeUpdateCommand,
  OrganizationTypeUpdatedEvent
} from "../model/OrganizationType";
import {useState} from "react";
import {authQuery} from "../../../utils/authQuery";

export const OrganizationTypeApi = createApi({
  reducerPath: 'organizationTypeApi',
  baseQuery: authQuery({baseUrl: config.baseUrl}),
  tagTypes: ['OrganizationType'],
  endpoints: (builder) => ({
    getOrganizationTypeById: builder.query<OrganizationType, OrganizationTypeId>({
      query: (id: OrganizationTypeId) => ({
        url: `organizationTypeGet`,
        method: 'POST',
        body: {id: id}
      }),
    }),
    organizationTypePage: builder.query<PageResult<OrganizationType>, PageQuery>({
      query: (page: PageQuery) => ({
        url: `organizationTypePage`,
        method: 'POST',
        body: page,
      }),
      transformResponse: (response: PageResult<OrganizationType>[]) => response[0],
      providesTags: ['OrganizationType'],
    }),
    organizationTypeCreate: builder.mutation<OrganizationTypeCreatedEvent, OrganizationTypeCreateCommand>({
      query: (cmd: OrganizationTypeCreateCommand) => ({
        url: `organizationTypeCreate`,
        method: 'POST',
        body: cmd
      }),
      transformResponse: (response: OrganizationTypeCreatedEvent[]) => response[0],
      invalidatesTags: ['OrganizationType'],
    }),
    organizationTypeUpdate: builder.mutation<OrganizationTypeUpdatedEvent, OrganizationTypeUpdateCommand>({
      query: (cmd: OrganizationTypeUpdateCommand) => ({
        url: `organizationTypeUpdate`,
        method: 'POST',
        body: cmd
      }),
      transformResponse: (response: OrganizationTypeUpdatedEvent[]) => response[0],
      invalidatesTags: ['OrganizationType'],
    }),
    organizationTypeDelete: builder.mutation<OrganizationTypeDeletedEvent, OrganizationTypeDeleteCommand>({
      query: (cmd: OrganizationTypeDeleteCommand) => ({
        url: `organizationTypeDelete`,
        method: 'POST',
        body: cmd
      }),
      transformResponse: (response: OrganizationTypeDeletedEvent[]) => response[0],
      invalidatesTags: ['OrganizationType'],
    }),
  }),
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useOrganizationTypePageQuery: useOrganizationTypePageQuery,
  useOrganizationTypeCreateMutation, useOrganizationTypeUpdateMutation, useOrganizationTypeDeleteMutation
} = OrganizationTypeApi

export const useGetOrganizationTypeAllQuery = (pagination?: OffsetPagination) => {
  const {data, error, isLoading, refetch} = OrganizationTypeApi.useOrganizationTypePageQuery({})
  const [pageQuery, setPageQuery] = useState<PageQuery>({
    pagination: pagination || allPage
  })
  let sortedData =  [...data?.items ?? []]
  sortedData.sort((a, b) => {
    return  a.name.localeCompare(b.name)
  })

  if(pageQuery.search) {
    sortedData = sortedData.filter((item) => {
      return item.name.toLowerCase().includes(pageQuery.search?.toLowerCase() ?? "")
    })
  }
  const page = toPagination(sortedData, pageQuery.pagination)

  return {data: page, error, isLoading, refetch, setPageQuery}
}